<script setup lang="ts">
import { formatMoneyWithSymbol } from '@/lib/money'
import { ref, nextTick } from 'vue'
import { CompetitorItemDetail, HistoricalData, Item } from '@/types'
import { competitorsApi } from '@/api/client'
import { forEach } from 'lodash'
import dayjs from 'dayjs'
import { Chart, ChartConfiguration, TooltipItem } from 'chart.js/auto'
import { useI18n } from 'vue-i18n'
import { onBeforeRouteUpdate } from 'vue-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
    ArrowLeft,
    ExternalLink,
    TrendingUp,
    HandCoins,
    Smartphone,
    Laptop,
    Watch,
    Tablet,
    Gamepad2,
} from 'lucide-vue-next'
import Grade from '@/components/Grade.vue'
import Battery from '@/components/Battery.vue'
import BrandLogo from '@/components/BrandLogo.vue'

const props = defineProps<{
    id: number
    itemId: number
}>()

const { t } = useI18n()

const detail = ref<CompetitorItemDetail | null>(null)
const items = ref<Item[]>([])
const historical = ref<HistoricalData[]>([])
const loadingDetail = ref(false)
const loadingHistorical = ref(false)
const loadingOnlineItems = ref(false)
const chartRef = ref<HTMLCanvasElement | null>(null)
let chartInstance: Chart | null = null

const formatLabel = (key: string) => key.replace(/_/g, ' ')

const loadDetail = async (vendorId: number, itemId: number) => {
    loadingDetail.value = true
    try {
        const response = await competitorsApi.item(vendorId, itemId)
        detail.value = response.data as CompetitorItemDetail
    } catch {
        detail.value = null
    } finally {
        loadingDetail.value = false
    }
}

const loadHistorical = async (vendorId: number, itemId: number) => {
    loadingHistorical.value = true
    try {
        const response = await competitorsApi.historical(vendorId, itemId)
        historical.value = response.data as HistoricalData[]
    } catch {
        historical.value = []
    } finally {
        loadingHistorical.value = false
        await nextTick()
        if (historical.value.length > 0) {
            renderChart(historical.value)
        }
    }
}

const loadOnlineItems = async (vendorId: number, itemId: number) => {
    loadingOnlineItems.value = true
    try {
        const response = await competitorsApi.onlineItems(vendorId, itemId)
        items.value = response.data as Item[]
    } catch {
        items.value = []
    } finally {
        loadingOnlineItems.value = false
    }
}

const renderChart = (data: HistoricalData[]) => {
    if (!chartRef.value) return

    if (chartInstance) {
        chartInstance.destroy()
    }

    const medianPrices: { x: number; y: number }[] = []
    const avgPrices: { x: number; y: number }[] = []
    const minPrices: { x: number; y: number }[] = []
    const maxPrices: { x: number; y: number }[] = []

    forEach(data, (price) => {
        const x = dayjs(price.date).unix()
        medianPrices.push({ x, y: price.median_price })
        avgPrices.push({ x, y: price.average_price })
        minPrices.push({ x, y: price.min_price })
        maxPrices.push({ x, y: price.max_price })
    })

    const symbol = detail.value?.currency_symbol ?? ''

    const config: ChartConfiguration<'scatter'> = {
        type: 'scatter',
        data: {
            datasets: [
                { label: 'Min', data: minPrices },
                { label: 'Median', data: medianPrices, hidden: true },
                { label: 'Max', data: maxPrices, hidden: true },
                { label: 'Avg', data: avgPrices, hidden: true },
            ],
        },
        options: {
            responsive: true,
            plugins: {
                legend: {
                    position: 'top',
                    title: { color: 'rgba(156, 163, 175, 1)' },
                },
                tooltip: {
                    callbacks: {
                        title: (tooltipItems: TooltipItem<'scatter'>[]) =>
                            tooltipItems[0].dataset.label + ' ' + dayjs.unix(Number(tooltipItems[0].parsed.x)).format('DD/MM'),
                        label: (item: TooltipItem<'scatter'>) => formatMoneyWithSymbol(item.parsed.y, symbol),
                    },
                },
            },
            scales: {
                y: {
                    border: { display: false },
                    grid: { color: 'rgba(156, 163, 175, 0.2)' },
                    ticks: {
                        callback: (value: string | number) => formatMoneyWithSymbol(Number(value), symbol),
                        color: 'rgba(156, 163, 175, 1)',
                    },
                },
                x: {
                    border: { display: false },
                    grid: { color: 'rgba(156, 163, 175, 0.2)' },
                    ticks: {
                        callback: (value: string | number) => dayjs.unix(Number(value)).format('DD/MM'),
                        color: 'rgba(156, 163, 175, 1)',
                    },
                },
            },
        },
    }

    chartInstance = new Chart(chartRef.value, config)
}

const loadAll = (vendorId: number, itemId: number) => {
    loadDetail(vendorId, itemId)
    loadHistorical(vendorId, itemId)
    loadOnlineItems(vendorId, itemId)
}

onBeforeRouteUpdate(async (to, from) => {
    if (to.params.id !== from.params.id || to.params.itemId !== from.params.itemId) {
        loadAll(Number(to.params.id), Number(to.params.itemId))
    }
})

loadAll(props.id, props.itemId)
</script>

<template>
    <div class="space-y-4">
        <div class="-mx-6 sm:-mx-8 border-b px-6 sm:px-8 pb-4">
            <router-link
                :to="{ name: 'dashboard.competitors.detail', params: { id: props.id } }"
                class="mb-3 inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
                <ArrowLeft class="size-4 mr-1" />
                {{ t('competitor.details.backToList') }}
            </router-link>

            <div class="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <h1 class="flex items-center gap-3 text-3xl font-bold tracking-tight">
                        <BrandLogo v-if="detail?.brand?.name" :brand="detail.brand.name" colored class="size-7" />
                        {{ detail?.name }}

                        <a
                            v-if="detail?.url"
                            :href="detail.url"
                            target="_blank"
                            class="inline-flex text-muted-foreground hover:text-foreground transition-colors align-middle ml-1"
                        >
                            <ExternalLink class="size-5" />
                        </a>
                    </h1>

                    <div v-if="detail" class="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
                        <span class="inline-flex items-center gap-1.5">
                            Grade: <Grade :grade="detail.grade" plain class="font-medium text-foreground" />
                        </span>

                        <span v-if="detail.details?.battery_type" class="inline-flex items-center gap-1.5">
                            Battery: <Battery :status="detail.details.battery_type" class="text-foreground font-medium" />
                        </span>

                        <span class="inline-flex items-center gap-1.5">
                            Price: <span class="font-medium text-foreground">{{ formatMoneyWithSymbol(detail.price, detail.currency_symbol) }}</span>
                        </span>

                        <template v-if="detail.details">
                            <template v-for="(value, name) in detail.details" :key="name">
                                <span
                                    v-if="name !== 'battery_type' && value"
                                    class="capitalize"
                                >
                                    {{ formatLabel(String(name)) }}: <span class="font-medium text-foreground uppercase">{{ value }}</span>
                                </span>
                            </template>
                        </template>
                    </div>
                </div>

                <div class="flex flex-wrap items-center gap-3">
                    <Badge v-if="detail?.vendor" :variant="detail.vendor.type === 'resell' ? 'resell' : 'tradein'">
                        <TrendingUp v-if="detail.vendor.type === 'resell'" class="size-3 mr-1" />
                        <HandCoins v-else class="size-3 mr-1" />
                        {{ detail.vendor.type === 'resell' ? 'Resell' : 'Trade-in' }}
                    </Badge>
                    <Badge v-if="detail?.vendor?.category" variant="outline">
                        <Smartphone v-if="detail.vendor.category.slug === 'smartphone'" class="size-3 mr-1" />
                        <Laptop v-else-if="detail.vendor.category.slug === 'pc'" class="size-3 mr-1" />
                        <Watch v-else-if="detail.vendor.category.slug === 'smartwatch'" class="size-3 mr-1" />
                        <Tablet v-else-if="detail.vendor.category.slug === 'tablet'" class="size-3 mr-1" />
                        <Gamepad2 v-else-if="detail.vendor.category.slug === 'console'" class="size-3 mr-1" />
                        {{ detail.vendor.category.name }}
                    </Badge>
                    <Badge variant="outline">
                        {{ detail?.vendor?.name }}
                    </Badge>
                    <Badge v-if="detail?.vendor?.country" variant="outline" class="uppercase">
                        {{ detail.vendor.country }}
                    </Badge>
                </div>
            </div>
        </div>
    </div>

    <div class="max-w-6xl space-y-6 mt-6">
        <!-- Historical Prices Chart -->
        <Card>
            <CardHeader>
                <CardTitle>{{ t('competitor.details.historicalPrices') }}</CardTitle>
            </CardHeader>
            <CardContent>
                <template v-if="loadingHistorical">
                    <Skeleton class="h-48 w-full" />
                </template>
                <template v-else-if="historical.length === 0">
                    <p class="text-sm text-muted-foreground py-8 text-center">
                        {{ t('competitor.details.historicalPricesEmpty') }}
                    </p>
                </template>
                <template v-else>
                    <canvas ref="chartRef" />
                </template>
            </CardContent>
        </Card>

        <!-- Online Items -->
        <Card>
            <CardHeader>
                <CardTitle class="flex items-center justify-between">
                    <span class="flex items-center gap-2">
                        {{ t('competitor.details.onlineItems.title') }}
                        <Badge v-if="items.length > 0" variant="secondary">
                            <span class="relative flex size-1.5 mr-1">
                                <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-50" />
                                <span class="relative inline-flex size-1.5 rounded-full bg-current" />
                            </span>
                            {{ items.length }}
                        </Badge>
                    </span>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <template v-if="loadingOnlineItems">
                    <div class="space-y-3">
                        <Skeleton class="h-8 w-full" />
                        <Skeleton class="h-8 w-full" />
                        <Skeleton class="h-8 w-full" />
                    </div>
                </template>
                <template v-else-if="items.length === 0">
                    <p class="text-sm text-muted-foreground">
                        {{ t('competitor.details.onlineItems.empty') }}
                    </p>
                </template>
                <template v-else>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{{ t('competitor.details.table.headings.competitor') }}</TableHead>
                                <TableHead>{{ t('competitor.details.table.headings.country') }}</TableHead>
                                <TableHead>{{ t('competitor.details.table.headings.price') }}</TableHead>
                                <TableHead>{{ t('competitor.details.table.headings.color') }}</TableHead>
                                <TableHead>{{ t('competitor.details.table.headings.link') }}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow v-for="item in items" :key="item.url">
                                <TableCell>
                                    <div class="text-sm">{{ item.vendor.name }}</div>
                                    <div v-if="item.seller_name" class="text-xs text-muted-foreground">{{ item.seller_name }}</div>
                                </TableCell>
                                <TableCell class="uppercase">{{ item.vendor.country }}</TableCell>
                                <TableCell class="font-medium tabular-nums">{{ formatMoneyWithSymbol(item.price, item.currency_symbol) }}</TableCell>
                                <TableCell class="capitalize">{{ item.color ?? '—' }}</TableCell>
                                <TableCell>
                                    <a
                                        v-if="item.url"
                                        :href="item.url"
                                        target="_blank"
                                        class="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                                    >
                                        <ExternalLink class="size-3.5" />
                                    </a>
                                    <span v-else class="text-muted-foreground">—</span>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </template>
            </CardContent>
        </Card>
    </div>
</template>
